/**
 * Green Flag — CancelRegistrationDialog
 *
 * Confirmation pop-up shown wherever a user can give up their own spot at an
 * event. It leads with the answer someone needs before pressing a destructive,
 * money-adjacent button — whether this cancellation gets a refund — and links
 * out to the full policy rather than reprinting it here.
 *
 * The verdict is computed from the event's start time with the same shared
 * helper `DELETE /spark/registration` uses to decide the refund, so the two
 * can't disagree.
 *
 * Deliberately no dollar figure: what someone actually paid lives on their
 * registration row (discounts, coupons, comps all move it), and the client
 * only has the event's list price. Rather than fetch that per cancellation,
 * the copy speaks of "anything you paid" — vaguer, but it can't misquote an
 * amount or promise a refund to someone who was never charged.
 *
 * The dialog owns the cancellation request itself so every entry point gets
 * the same confirmation, copy, and error handling. Callers just open it and
 * react to `onCancelled`.
 */

import {
  Box,
  Button,
  DialogBackdrop,
  DialogContent,
  DialogPositioner,
  DialogRoot,
  Flex,
  Heading,
  Text,
} from "@chakra-ui/react";
import {
  SPARK_REFUND_CUTOFF_HOURS,
  isSparkCancellationRefundable,
} from "@greenflagdate/shared";
import { useReq } from "@larner.dev/use-req";
import { apiReq } from "../../lib/apiReq";
import { formatEventWhen } from "../../lib/formatEventWhen";
import { Link } from "../Link/Link";

export interface CancelRegistrationDialogProps {
  open: boolean;
  sparkId: number;
  /** Event title, shown so the user can see what they're giving up. */
  title: string;
  /** Event start, ISO — decides the refund verdict. */
  startsAt: string;
  /** Close without cancelling (backdrop, escape, or "Keep my spot"). */
  onClose: () => void;
  /** The registration is gone — refresh whatever showed it. */
  onCancelled: () => void;
}

/**
 * What happens to the user's money: the `verdict` is the answer they came for
 * (bolded, and coloured when it's bad news), `detail` is the reasoning.
 */
function refundCopy(startsAt: string): {
  verdict: string;
  detail: string;
  losingMoney: boolean;
} {
  return isSparkCancellationRefundable(startsAt)
    ? {
        verdict: "You'll get a full refund of anything you paid.",
        detail: `You're cancelling at least ${SPARK_REFUND_CUTOFF_HOURS} hours before the event starts. Refunds can take a few business days to reach your card.`,
        losingMoney: false,
      }
    : {
        verdict: "You won't get a refund.",
        detail: `This event starts in less than ${SPARK_REFUND_CUTOFF_HOURS} hours, which is inside our cancellation window.`,
        losingMoney: true,
      };
}

export function CancelRegistrationDialog({
  open,
  sparkId,
  title,
  startsAt,
  onClose,
  onCancelled,
}: CancelRegistrationDialogProps) {
  const [cancelReq, cancelReqState] = useReq(apiReq);

  const handleConfirm = async () => {
    const result = await cancelReq.delete("/spark/registration", {
      spark_id: sparkId,
    });
    if (result.success) onCancelled();
  };

  const refund = refundCopy(startsAt);

  return (
    <DialogRoot
      open={open}
      onOpenChange={(d) => {
        if (!d.open && !cancelReqState.loading) onClose();
      }}
    >
      <DialogBackdrop />
      <DialogPositioner>
        <DialogContent
          maxW="25rem"
          w="calc(100% - 2rem)"
          mx="auto"
          my="2rem"
          maxH="calc(100dvh - 4rem)"
          borderRadius="xl"
          bg="bg.canvas"
          overflowY="auto"
        >
          <Box padding="1.5rem">
            <Heading as="h2" textStyle="headingSm" mb="0.5rem">
              Cancel your registration?
            </Heading>
            <Text textStyle="bodyMuted" mb="1rem">
              You'll give up your spot at {title} on{" "}
              {formatEventWhen(startsAt)}. You can register again later if
              there's still room.
            </Text>

            <Box
              bg="bg.subtle"
              borderRadius="lg"
              padding="0.75rem"
              mb="1rem"
              data-testid="cancel-registration-refund"
            >
              <Text textStyle="eyebrow" color="fg.muted" mb="0.375rem">
                Your refund
              </Text>
              <Text
                textStyle="body"
                fontWeight="bold"
                color={refund.losingMoney ? "fg.danger" : "fg.default"}
              >
                {refund.verdict}
              </Text>
              <Text textStyle="bodyLight" color="fg.muted" mt="0.25rem">
                {refund.detail}
              </Text>
            </Box>

            {/* The canonical policy page (same target as the footer link).
                Opens in a new tab so reading it doesn't navigate the user out
                of a half-finished cancellation. */}
            <Box mb="1.5rem">
              <Link to="/policy/refunds" target="_blank" textStyle="bodyLight">
                Read our full refund policy
              </Link>
            </Box>

            {cancelReqState.error && (
              <Text textStyle="caption" color="fg.danger" mb="1rem" role="alert">
                Something went wrong cancelling. Please try again.
              </Text>
            )}

            <Flex gap="0.75rem" direction={{ base: "column-reverse", sm: "row" }}>
              <Button
                flex="1"
                variant="outline"
                onClick={onClose}
                disabled={cancelReqState.loading}
              >
                Keep my spot
              </Button>
              <Button
                flex="1"
                // The Button recipe's `solid` hardcodes the brand green, so a
                // colorPalette won't turn this red — set the surface directly.
                bg="bg.danger"
                color="fg.onBrand"
                loading={cancelReqState.loading}
                onClick={handleConfirm}
              >
                Yes, cancel registration
              </Button>
            </Flex>
          </Box>
        </DialogContent>
      </DialogPositioner>
    </DialogRoot>
  );
}
